import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Project, ProjectVisibility } from '../types';

interface Props {
  project: Project;
  onClose: () => void;
  onSaved: (updated: Project) => void;
}

function safeUrl(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

export default function EditProjectModal({ project, onClose, onSaved }: Props) {
  const { role } = useAuth();
  const isDeveloper = role === 'developer';

  const [name,        setName]        = useState(project.name);
  const [description, setDescription] = useState(project.description ?? '');
  const [link,        setLink]        = useState(project.link ?? '');
  const [visibility,  setVisibility]  = useState<ProjectVisibility>(project.visibility);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Project name is required.'); return; }
    setError('');
    setSaving(true);

    try {
      const updated = await api.patch<Project>(`/api/projects/${project.id}`, {
        name:        name.trim(),
        description: description.trim(),
        link:        safeUrl(link) || null,
        visibility:  isDeveloper ? project.visibility : visibility,
      });
      setSaving(false);
      onSaved(updated);
    } catch (err: any) {
      setSaving(false);
      setError(err.message || 'Failed to save changes.');
    }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel edit-project-modal" role="dialog" aria-modal="true" aria-labelledby="edit-project-title">
        <div className="modal-header">
          <div className="modal-header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 id="edit-project-title" className="modal-title">Edit Project</h2>
            <p className="modal-subtitle">{project.name}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSave}>
          <div className="modal-body">
            {/* Name */}
            <div className="form-group">
              <label className="form-label" htmlFor="ep-name">
                Project Name <span className="required">*</span>
              </label>
              <input
                id="ep-name" ref={nameRef}
                type="text" className="form-input"
                value={name} onChange={e => setName(e.target.value)}
                maxLength={120} required
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label className="form-label" htmlFor="ep-desc">
                Description <span className="form-label-hint">(optional)</span>
              </label>
              <textarea
                id="ep-desc" className="form-textarea" rows={3}
                placeholder="What is this project about?"
                value={description} onChange={e => setDescription(e.target.value)}
              />
            </div>

            {/* Link */}
            <div className="form-group">
              <label className="form-label" htmlFor="ep-link">
                Project Link <span className="form-label-hint">(optional)</span>
              </label>
              <div className="input-with-icon">
                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                  <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                </svg>
                <input
                  id="ep-link"
                  type="url"
                  className="form-input input-with-icon-field"
                  placeholder="https://github.com/org/repo"
                  value={link}
                  onChange={e => setLink(e.target.value)}
                />
              </div>
              <span className="form-hint">Link to a repo, Figma file, doc, or any related resource</span>
            </div>

            {/* Visibility */}
            {!isDeveloper && (
              <div className="form-group">
                <label className="form-label">Visibility</label>
                <div className="visibility-toggle">
                  <button
                    type="button"
                    className={`visibility-option${visibility === 'public' ? ' active' : ''}`}
                    onClick={() => setVisibility('public')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
                    </svg>
                    Public
                    <span className="visibility-hint">All company members</span>
                  </button>
                  <button
                    type="button"
                    className={`visibility-option${visibility === 'private' ? ' active' : ''}`}
                    onClick={() => setVisibility('private')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                    Private
                    <span className="visibility-hint">Only you & invited members</span>
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="invite-feedback error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                  <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                {error}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !name.trim()}>
              {saving ? <><span className="btn-spinner" /> Saving…</> : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
